// filepath: c:\Users\Tcvmak\Devsachi\Live\LIVEMAY\routinefinder\sachi-animations.js
/**
 * Consolidated Animations for Sachi Routine Finder
 * 
 * This file contains animations and transitions for the Routine Finder.
 * ALL image animations have been completely removed to simplify the code and improve performance.
 * Images are now updated directly without animation effects.
 * 
 * Sections:
 * 1. Core Animation Utilities
 * 2. Section Transitions
 * 3. Cart Functionality & Animations
 * 4. Results Page Animations
 * 5. Performance Optimizations
 * 6. Initialization & Setup
 */

// Create the global namespace IMMEDIATELY and explicitly - this must be the very first thing
if (typeof window !== 'undefined') {
  // Initialize the global namespace immediately (before DOM load)
  window.sachiAnimations = window.sachiAnimations || {};
  
  // Create animation helpers namespace for backward compatibility
  window.animationHelpers = window.animationHelpers || {};
    // Global flags for initialization status
  window.sachiAnimations._initialized = false;
    // Ensure spinner CSS is available immediately with improved visibility
  const spinnerStyle = document.createElement('style');
  spinnerStyle.textContent = `
    .spinner {
      display: inline-block;
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255,255,255,0.3);
      border-radius: 50%;
      border-top-color: #fff;
      animation: spin 0.8s ease-in-out infinite;
      vertical-align: middle;
      margin-right: 5px;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    /* Enhanced loading indicator styles */
    .loading-indicator {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.9) !important;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 10000 !important;
      box-shadow: 0 0 40px rgba(0, 0, 0, 0.1);
    }
    
    /* Enhanced loading indicator spinner */
    .loading-indicator .spinner {
      width: 40px;
      height: 40px;
      border-width: 3px;
      margin-bottom: 15px;
      border-top-color: #c3785b;
    }
  `;
  document.head.appendChild(spinnerStyle);
  
  // Log when script is loaded
  console.log("sachi-animations.js loaded - sachiAnimations global object created");
  
  // Check for formQuestions
  if (typeof window.formQuestions !== 'undefined') {
    console.log("formQuestions already available when animations loaded");
    console.log("Number of questions:", window.formQuestions.length);
  } else {
    console.log("formQuestions not available yet, will check again later");
    
    // Add a small delay to check if formQuestions loads later
    setTimeout(function() {
      if (typeof window.formQuestions !== 'undefined') {
        console.log("formQuestions available after delay");
        console.log("Number of questions:", window.formQuestions.length);
      } else {
        console.warn("formQuestions still not available after delay");
      }
    }, 500);
  }
}

document.addEventListener('DOMContentLoaded', function() {
  /*****************************************************************************
   * 1. CORE ANIMATION UTILITIES
   *****************************************************************************/
  
  // Create a global namespace for animation utilities
  window.sachiAnimations = window.sachiAnimations || {};
  
  // Helper function to animate numbers (for progress bar, etc.)
  function animateNumber(from, to, duration, callback) {
    const start = performance.now();
    
    function updateNumber(timestamp) {
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuad = progress * (2 - progress);
      const current = from + (to - from) * easeOutQuad;
      
      callback(current);
      
      if (progress < 1) {
        requestAnimationFrame(updateNumber);
      }
    }
    
    requestAnimationFrame(updateNumber);
  }
  
  // Helper function to optimize animations
  function optimizeElement(element) {
    if (!element) return;
    
    // Use will-change only during animations
    element.style.willChange = 'opacity';
    
    // Reset will-change after animations complete
    element.addEventListener('transitionend', function() {
      setTimeout(() => {
        element.style.willChange = 'auto';
      }, 200);
    }, { once: true });
  }
  
  // Fix progress bar animation
  function fixProgressBar() {
    const progressFill = document.getElementById('progressFill');
    const progressText = document.getElementById('progressPercentage');
    
    if (!progressFill) return;
    
    // Ensure smooth transition with spring effect
    progressFill.style.transition = 'width 0.4s ease';
    
    // Update progress bar animation function
    window.sachiAnimations.updateProgressBar = function(percent) {
      if (!progressFill) return;
      
      // Add a slight scale effect during transition
      progressFill.style.transform = 'scaleY(1.05)';
      setTimeout(() => {
        progressFill.style.transform = 'scaleY(1)';
      }, 200);
      
      // Set the width with a smooth transition
      progressFill.style.width = `${percent}%`;
      
      // Animate the percentage text if it exists
      if (progressText) {
        const currentPercent = parseInt(progressText.textContent) || 0;
        animateNumber(currentPercent, percent, 400, function(value) {
          progressText.textContent = `${Math.round(value)}%`;
        });
      }
    };
  }
  
  /*****************************************************************************
   * 2. SECTION TRANSITIONS
   *****************************************************************************/
  
  // Handle smooth transitions between sections
  function setupSectionTransitions() {
    // Enhanced section transition function
    window.sachiAnimations.transitionSections = function(fromSection, toSection) {
      if (!fromSection || !toSection) return;
      
      // Determine transition direction based on sections
      const goingForward = fromSection.id === 'introSection' || 
                          (fromSection.id === 'formSection' && toSection.id === 'emailSection') ||
                          (fromSection.id === 'emailSection' && toSection.id === 'resultsSection');
      
      // Apply different animation based on direction
      if (goingForward) {
        // Fade and slide out current section
        fromSection.style.opacity = '0';
        fromSection.style.transform = 'translateX(-20px)';
        fromSection.style.transition = 'opacity 0.45s ease, transform 0.5s ease';
      } else {
        // Fade out for backward navigation
        fromSection.style.opacity = '0';
        fromSection.style.transform = 'translateX(20px)';
        fromSection.style.transition = 'opacity 0.45s ease, transform 0.5s ease';
      }
      
      // Short delay before swapping sections
      setTimeout(() => {
        // Hide all sections
        const allSections = document.querySelectorAll('.section');
        allSections.forEach(section => {
          section.classList.remove('active');
          section.style.transform = '';
        });
        // Show target section
        toSection.classList.add('active');
        
        // Set initial opacity and transform for target section
        toSection.style.opacity = '0';
        toSection.style.transform = goingForward ? 'translateX(20px)' : 'translateX(-20px)';
        toSection.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        
        // Trigger animation with slight delay
        setTimeout(() => {
          toSection.style.opacity = '1';
          toSection.style.transform = 'translateX(0)';
          
          // Reset transition properties after animation completes
          setTimeout(() => {
            toSection.style.transition = '';
          }, 600);
          
          // If transitioning to results, trigger results animations
          if (toSection.id === 'resultsSection') {
            enhanceResultsPageAnimations();
          }
        }, 50);
      }, 450);
    };
  }
  
  // Email section functionality
  function setupEmailFunctionality() {
    // Helper function to remove any existing loading indicators
    function cleanupLoadingIndicators() {
      const existingIndicators = document.querySelectorAll('.loading-indicator');
      existingIndicators.forEach(indicator => {
        try {
          document.body.removeChild(indicator);
        } catch (e) {
          console.error("Error removing loading indicator:", e);
        }
      });
      // Also clean up global indicator if it exists
      if (window.sachiLoadingIndicator) {
        try {
          if (document.body.contains(window.sachiLoadingIndicator)) {
            document.body.removeChild(window.sachiLoadingIndicator);
          }
          window.sachiLoadingIndicator = null;
        } catch (e) {
          console.error("Error removing global loading indicator:", e);
        }
      }
    }

    // Helper function to show loading indicator
    function showLoadingIndicator() {
      // Clean up any existing indicators first
      cleanupLoadingIndicators();
      
      // Create a more visually prominent loading indicator
      const loadingIndicator = document.createElement('div');
      loadingIndicator.className = 'loading-indicator';
      
      loadingIndicator.innerHTML = `
        <div class="spinner"></div>
        <p class="loading-primary-text">Creating your personalized routine</p>
        <p class="loading-secondary-text">This may take a few moments</p>
        <div class="loading-progress-container">
          <div class="loading-progress-bar"></div>
        </div>
      `;
      
      // Save reference globally so it can be accessed by other functions
      window.sachiLoadingIndicator = loadingIndicator;
      
      // Add the loading indicator to the body
      document.body.appendChild(loadingIndicator);
      
      // Ensure the loading indicator is fully visible
      requestAnimationFrame(() => {
        loadingIndicator.style.opacity = '1';
      });

      return loadingIndicator;
    }

    // Handle form submission
    window.handleEmailSubmit = function(event, email) {
      if (event) event.preventDefault();
      
      // Validate email
      if (!email || !email.includes('@')) {
        if (typeof showToast === 'function') {
          showToast("Please enter a valid email address");
        } else if (window.showToast) {
          window.showToast("Please enter a valid email address");
        }
        return;
      }
      
      // Display loading state
      const submitBtn = document.getElementById('submitEmailBtn');
      if (submitBtn) {
        submitBtn.innerHTML = '<span class="spinner"></span> Processing...';
        submitBtn.disabled = true;
      }
      
      // Show loading indicator
      const loadingIndicator = showLoadingIndicator();
      
      // Short delay to show loading state
      setTimeout(() => {
        // Check if recommendations are available
        if (window.recommendations) {
          // Save email for potential marketing
          console.log("Email submitted:", email);
          
          // Ensure the results section is shown
          const resultsSection = document.getElementById('resultsSection');
          const emailSection = document.getElementById('emailSection');
          
          if (resultsSection && emailSection) {
            // Basic transition to make sure it works
            emailSection.classList.remove('active');
            resultsSection.classList.add('active');
            
            // Trigger enhanced results animations
            enhanceResultsPageAnimations();
          }
          
          // Reset email form
          if (submitBtn) {
            submitBtn.innerHTML = 'Submit';
            submitBtn.disabled = false;
          }
          
          // Clean up loading indicator with animation
          setTimeout(() => {
            cleanupLoadingIndicators();
          }, 500); // Give time for the results to be visible
          
        } else {
          if (window.showToast) window.showToast("An error occurred while generating your routine. Please try again.");
          
          if (submitBtn) {
            submitBtn.innerHTML = 'Submit';
            submitBtn.disabled = false;
          }
          
          // Clean up loading indicator immediately on error
          cleanupLoadingIndicators();
        }
      }, 800);
    };

    // Fix skip email too
    window.handleSkipEmail = function() {
      const resultsSection = document.getElementById('resultsSection');
      const emailSection = document.getElementById('emailSection');
      
      if (resultsSection && emailSection) {
        // Basic transition to make sure it works
        emailSection.classList.remove('active');
        resultsSection.classList.add('active');
        
        // Trigger enhanced results animations
        enhanceResultsPageAnimations();
      }
      
      // Scroll to the top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  }
  
  /*****************************************************************************
   * 3. CART FUNCTIONALITY & ANIMATIONS
   *****************************************************************************/
  
  // Setup Add to Cart animations and functionality
  function setupCartFunctionality() {
    // Fix Add to Cart functionality for Essential tab
    const addToCartBtn = document.getElementById('addToCartBtn');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', function() {
        // Show loading state
        const originalText = this.innerHTML;
        
        // Create spinner element directly
        const spinner = document.createElement('span');
        spinner.className = 'spinner';
        
        // Clear the button and add spinner + text
        this.innerHTML = '';
        this.appendChild(spinner);
        this.appendChild(document.createTextNode(' Adding...'));
        this.disabled = true;
        
        // Use Shopify helpers if available
        if (window.shopifyHelpers && window.shopifyHelpers.addProductsToCart) {
          try {
            const products = window.recommendations ? window.recommendations.essentialProducts : [];
            if (products && products.length > 0) {
              window.shopifyHelpers.addProductsToCart(products)
                .then(() => {
                  if (window.showToast) window.showToast("Products added to cart!");
                  this.innerHTML = '<span class="checkmark">✓</span> Added to Cart';
                  setTimeout(() => {
                    this.innerHTML = originalText;
                    this.disabled = false;
                  }, 2000);
                })
                .catch(err => {
                  console.error("Error adding products to cart:", err);
                  if (window.showToast) window.showToast("Error adding products to cart. Please try again.");
                  this.innerHTML = originalText;
                  this.disabled = false;
                });
            } else {
              if (window.showToast) window.showToast("No products to add to cart.");
              this.innerHTML = originalText;
              this.disabled = false;
            }
          } catch (err) {
            console.error("Error in add to cart flow:", err);
            this.innerHTML = originalText;
            this.disabled = false;
          }
        } else {
          console.warn("shopifyHelpers not available for cart operations");
          this.innerHTML = originalText;
          this.disabled = false;
        }
      });
      
      // Add hover effects for Essential button
      addToCartBtn.addEventListener('mouseenter', function() {
        if (this.disabled) return;
        this.style.transform = 'scale(1.03)';
        this.style.boxShadow = '0 6px 12px rgba(195, 120, 91, 0.25)';
      });
      
      addToCartBtn.addEventListener('mouseleave', function() {
        if (this.disabled) return;
        this.style.transform = 'scale(1)';
        this.style.boxShadow = '';
      });
    }

    // REMOVE THE ADVANCED BUTTON FUNCTIONALITY - Let main-routinefinder.js handle it
    // Only add hover effects for Advanced button (no click handler)
    const addToCartBtnAdvanced = document.getElementById('addToCartBtnAdvanced');
    if (addToCartBtnAdvanced) {
      // Add hover effects for Advanced button
      addToCartBtnAdvanced.addEventListener('mouseenter', function() {
        if (this.disabled) return;
        this.style.transform = 'scale(1.03)';
        this.style.boxShadow = '0 6px 12px rgba(195, 120, 91, 0.25)';
      });
      
      addToCartBtnAdvanced.addEventListener('mouseleave', function() {
        if (this.disabled) return;
        this.style.transform = 'scale(1)';
        this.style.boxShadow = '';
      });
    }
    
    // Enhanced individual product "Add to Cart" buttons
    enhanceSingleProductButtons();
  }
  
  // Enhance single product buttons with animations
  function enhanceSingleProductButtons() {
    const singleAddButtons = document.querySelectorAll('.add-single-product');
    singleAddButtons.forEach(button => {
      // Make sure we're not double-attaching listeners
      if (button.getAttribute('data-enhanced') === 'true') return;
      
      button.setAttribute('data-enhanced', 'true');
      
      // Add hover effects
      button.addEventListener('mouseenter', function() {
        if (this.disabled) return;
        this.style.transform = 'translateY(-2px)';
        this.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
      });
      
      button.addEventListener('mouseleave', function() {
        if (this.disabled) return;
        this.style.transform = '';
        this.style.boxShadow = '';
      });
    });
  }
  
  /*****************************************************************************
   * 4. RESULTS PAGE ANIMATIONS
   *****************************************************************************/
  
  // Enhanced animations for results page
  function enhanceResultsPageAnimations() {
    const resultsSection = document.getElementById('resultsSection');
    if (!resultsSection || !resultsSection.classList.contains('active')) return;
    
    // Mark that we've run the results animations
    window.sachiAnimations.resultsAnimatedBy = 'consolidated';

    // Animate the main heading with slide-in
    const resultsHeading = resultsSection.querySelector('h2');
    if (resultsHeading) {
      resultsHeading.style.opacity = '0';
      resultsHeading.style.transform = 'translateY(-20px)';
      resultsHeading.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
      
      setTimeout(() => {
        resultsHeading.style.opacity = '1';
        resultsHeading.style.transform = 'translateY(0)';
      }, 100);
    }
    
    // Animate routine products with staggered entrance
    const products = resultsSection.querySelectorAll('.recommended-product, .product-card');
    products.forEach((product, index) => {
      product.style.opacity = '0';
      product.style.transform = 'translateY(20px)';
      product.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      
      setTimeout(() => {
        product.style.opacity = '1';
        product.style.transform = 'translateY(0)';
      }, 200 + (index * 100)); // Staggered delay based on index
    });
    
    // Animate routine steps with fade and slide
    const routineSteps = resultsSection.querySelectorAll('.routine-step, .step-container');
    routineSteps.forEach((step, index) => {
      step.style.opacity = '0';
      step.style.transform = 'translateX(-10px)';
      step.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
      
      setTimeout(() => {
        step.style.opacity = '1';
        step.style.transform = 'translateX(0)';
      }, 300 + (index * 150)); // Longer staggered delay for steps
    });
    
    // Apply animations to individual product items
    animateIndividualProducts();
    
    // Animate tab content if tabs exist
    const tabButtons = resultsSection.querySelectorAll('.tab-button, .tab-nav-item');
    const tabContents = resultsSection.querySelectorAll('.tab-content');
    
    // Fade in tab buttons with slight delay
    tabButtons.forEach((button, index) => {
      button.style.opacity = '0';
      button.style.transition = 'opacity 0.5s ease';
      
      setTimeout(() => {
        button.style.opacity = '1';
      }, 400 + (index * 100));
    });
    
    // Animate the active tab content
    const activeTabContent = Array.from(tabContents).find(tab => 
      window.getComputedStyle(tab).display !== 'none'
    );
    
    if (activeTabContent) {
      activeTabContent.style.opacity = '0';
      activeTabContent.style.transform = 'translateY(10px)';
      activeTabContent.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
      
      setTimeout(() => {
        activeTabContent.style.opacity = '1';
        activeTabContent.style.transform = 'translateY(0)';
      }, 500);
    }
    
    // Animate the Add to Cart button (Essential)
    const addToCartBtn = document.getElementById('addToCartBtn');
    if (addToCartBtn) {
      addToCartBtn.style.opacity = '0';
      addToCartBtn.style.transform = 'scale(0.95)';
      addToCartBtn.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
      
      setTimeout(() => {
        addToCartBtn.style.opacity = '1';
        addToCartBtn.style.transform = 'scale(1)';
      }, 800);
    }

    // Animate the Advanced Add to Cart button
    const addToCartBtnAdvanced = document.getElementById('addToCartBtnAdvanced');
    if (addToCartBtnAdvanced) {
      addToCartBtnAdvanced.style.opacity = '0';
      addToCartBtnAdvanced.style.transform = 'scale(0.95)';
      addToCartBtnAdvanced.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
      
      setTimeout(() => {
        addToCartBtnAdvanced.style.opacity = '1';
        addToCartBtnAdvanced.style.transform = 'scale(1)';
      }, 800);
    }
  }
  
  // Animate individual product items with staggered effect
  function animateIndividualProducts() {
    const productElements = document.querySelectorAll('.routine-step');
    productElements.forEach((product, index) => {
      // Skip bundle items which are handled separately
      if (product.classList.contains('bundle-item')) return;
      
      // Get the product components
      const productTitle = product.querySelector('.product-title');
      const productDesc = product.querySelector('.product-description');
      const addButton = product.querySelector('.add-single-product');
      
      // Set initial states for animation
      if (productTitle) {
        productTitle.style.opacity = '0';
        productTitle.style.transform = 'translateY(-5px)';
        productTitle.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
      }
      
      if (productDesc) {
        productDesc.style.opacity = '0';
        productDesc.style.transition = 'opacity 0.5s ease';
      }
      
      if (addButton) {
        addButton.style.opacity = '0';
        addButton.style.transform = 'translateY(5px)';
        addButton.style.transition = 'opacity 0.5s ease, transform 0.5s ease, background-color 0.3s ease, box-shadow 0.3s ease';
      }
      
      // Animate with staggered timing
      setTimeout(() => {
        // Stagger the elements
        setTimeout(() => {
          if (productTitle) {
            productTitle.style.opacity = '1';
            productTitle.style.transform = 'translateY(0)';
          }
          
          setTimeout(() => {
            if (productDesc) {
              productDesc.style.opacity = '1';
            }
            
            setTimeout(() => {
              if (addButton) {
                addButton.style.opacity = '1';
                addButton.style.transform = 'translateY(0)';
                
                // Add hover effect
                addButton.addEventListener('mouseenter', function() {
                  if (!this.disabled) {
                    this.style.transform = 'translateY(-2px)';
                    this.style.boxShadow = '0 4px 8px rgba(195, 120, 91, 0.25)';
                  }
                });
                
                addButton.addEventListener('mouseleave', function() {
                  if (!this.disabled) {
                    this.style.transform = 'translateY(0)';
                    this.style.boxShadow = '';
                  }
                });
              }
            }, 150);
          }, 100);
        }, 200);
      }, 300 + (index * 150)); // Staggered by product
    });
  }
  
  /*****************************************************************************
   * 5. PERFORMANCE OPTIMIZATIONS
   *****************************************************************************/
  
  // Optimize animations for better performance
  function applyPerformanceOptimizations() {
    // Optimize add to cart button animations
    optimizeButtonAnimations();
    
    // Apply subtle attention effect for important elements
    setTimeout(() => {
      const addToCartBtn = document.getElementById('addToCartBtn');
      if (addToCartBtn) {
        pulseImportantButton(addToCartBtn);
      }
      
      // Also apply to Advanced button
      const addToCartBtnAdvanced = document.getElementById('addToCartBtnAdvanced');
      if (addToCartBtnAdvanced) {
        setTimeout(() => {
          pulseImportantButton(addToCartBtnAdvanced);
        }, 200); // Slight delay between the two buttons
      }
    }, 1500);
  }
  
  // Optimize button animations for better performance
  function optimizeButtonAnimations() {
    const buttons = document.querySelectorAll('button, .button, .btn');
    
    buttons.forEach(button => {
      // Skip if already optimized
      if (button.hasAttribute('data-optimized')) return;
      button.setAttribute('data-optimized', 'true');
      
      // Use will-change only during hover or animations
      button.addEventListener('mouseenter', function() {
        if (!this.disabled) {
          this.style.willChange = 'transform, box-shadow';
        }
      });
      
      button.addEventListener('mouseleave', function() {
        setTimeout(() => {
          this.style.willChange = 'auto';
        }, 200);
      });
    });
  }
  
  // Add subtle attention effect for important buttons
  function pulseImportantButton(button) {
    if (!button || button.disabled) return;
    
    // Add pulse effect
    const pulse = () => {
      button.style.transform = 'scale(1.05)';
      setTimeout(() => {
        button.style.transform = 'scale(1)';
      }, 300);
    };
    
    // Apply pulse with slight delay
    setTimeout(pulse, 300);
  }
  
  /*****************************************************************************
   * 6. INITIALIZATION & SETUP
   *****************************************************************************/
  // Main initialization function
  function initializeAnimations() {
    console.log("Sachi Animations: Initializing consolidated animations...");
    
    // Mark the animations as initializing
    window.sachiAnimations.isInitializing = true;
    
    // Add utility functions to the global namespace for backwards compatibility
    setupBackwardsCompatibility();
    
    // Initialize progress bar
    fixProgressBar();
    
    // Initialize section transitions
    setupSectionTransitions();
    
    // Initialize email functionality 
    setupEmailFunctionality();
    
    // Initialize cart functionality
    setupCartFunctionality();
    
    // Apply performance optimizations
    applyPerformanceOptimizations();
    
    // Set up observers to watch for DOM changes
    setupObservers();
    
    // Mark the animations as initialized
    window.sachiAnimations.isInitializing = false;
    window.sachiAnimations.isInitialized = true;
    window.sachiAnimations._initialized = true;
    
    console.log("Sachi Animations: Initialization complete");
  }
    // Setup backwards compatibility with previous animation files
  function setupBackwardsCompatibility() {
    // Animation Helpers compatibility
    window.animationHelpers = window.animationHelpers || {};
    window.animationHelpers.animateQuestionText = window.sachiAnimations.animateQuestionText;
    window.animationHelpers.animateOptions = window.sachiAnimations.animateOptions;
    window.animationHelpers.animateProgressBar = window.sachiAnimations.updateProgressBar;
    window.animationHelpers.animateResults = enhanceResultsPageAnimations;
    window.animationHelpers.smoothSectionTransition = window.sachiAnimations.transitionSections;
    
    // Direct button animation helper
    window.sachiAnimations.showSpinner = function(buttonElement, text) {
      if (!buttonElement) return;
      
      // Create spinner element
      const spinner = document.createElement('span');
      spinner.className = 'spinner';
      
      // Clear button and add spinner + text
      buttonElement.innerHTML = '';
      buttonElement.appendChild(spinner);
      buttonElement.appendChild(document.createTextNode(' ' + (text || 'Loading...')));
      buttonElement.disabled = true;
    };
    
    window.sachiAnimations.hideSpinner = function(buttonElement, text) {
      if (!buttonElement) return;
      buttonElement.innerHTML = text || 'Submit';
      buttonElement.disabled = false;
    };
      // Add simple non-animated image handling function for backwards compatibility
    window.sachiAnimations.crossfadeImages = function(questionId, altText) {
      console.log("Direct image update (no animation) for:", questionId);
      
      // Find the question with this ID
      let imagePath = '';
      if (window.formQuestions) {
        const question = window.formQuestions.find(q => q.id === questionId);
        if (question && question.imagePath) {
          imagePath = question.imagePath;
        }
      }
      
      // If no path found, try using the question ID as the path
      if (!imagePath) {
        imagePath = `https://cdn.shopify.com/s/files/1/0236/1258/8078/files/${questionId}.jpg`;
      }
      
      // Update current image directly - no animation needed
      const currentImage = document.getElementById('questionImageCurrent');
      if (currentImage) {
        // Set all properties at once for better performance
        currentImage.src = imagePath;
        currentImage.alt = altText || `Sachi Skin - ${questionId}`;
        currentImage.setAttribute('data-current', imagePath);
        
        // Ensure visibility - no transition
        if (currentImage.style.opacity !== '1') currentImage.style.opacity = '1';
        if (!currentImage.classList.contains('active')) currentImage.classList.add('active');
      }
      
      // Update the next image as well for consistency, but keep it hidden
      const nextImage = document.getElementById('questionImageNext');
      if (nextImage) {
        nextImage.src = imagePath;
        nextImage.alt = altText || `Sachi Skin - ${questionId}`;
        
        // Ensure it's hidden - no transition
        if (nextImage.style.opacity !== '0') nextImage.style.opacity = '0';
        if (nextImage.classList.contains('active')) nextImage.classList.remove('active');
      }
    };
      // Add crossfadeImages to animationHelpers for backwards compatibility
    window.animationHelpers.crossfadeImages = window.sachiAnimations.crossfadeImages;
    
    // Transition Utils compatibility
    window.transitionUtils = window.transitionUtils || {};
    window.transitionUtils.animateQuestionText = window.sachiAnimations.animateQuestionText;
    window.transitionUtils.animateOptions = window.sachiAnimations.animateOptions;
    window.transitionUtils.animateProgressBar = window.sachiAnimations.updateProgressBar;
    window.transitionUtils.updateQuestionImageWithTransition = window.sachiAnimations.crossfadeImages;
    
    // Animation Coordinator compatibility
    window.animationCoordinator = window.animationCoordinator || {};
    window.animationCoordinator.resultsAnimatedBy = null;
    window.animationCoordinator.init = function() {
      console.log("Animation Coordinator: Using consolidated animations instead");
    };
  }
  
  // Create animation utilities on the global namespace
  window.sachiAnimations = window.sachiAnimations || {};
  
  // Add animation methods to global namespace
  window.sachiAnimations.animateQuestionText = function() {
    const questionTitle = document.getElementById('questionTitle');
    const questionDescription = document.getElementById('questionDescription');
    
    if (questionTitle) {
      questionTitle.classList.remove('slide-in');
      void questionTitle.offsetWidth; // Force reflow
      questionTitle.classList.add('slide-in');
    }
    
    if (questionDescription) {
      questionDescription.classList.remove('slide-in');
      void questionDescription.offsetWidth; // Force reflow
      questionDescription.classList.add('slide-in');
      questionDescription.style.animationDelay = '0.1s';
    }
  };
  
  // Add staggered animations to options
  window.sachiAnimations.animateOptions = function() {
    // Remove all animation classes and delays from options
    const options = document.querySelectorAll('.option');
    options.forEach(option => {
      option.style.animationDelay = '0s';
      option.classList.remove('fade-in', 'slide-in', 'slide-up');
    });
  };
  
  // Setup observers to watch for DOM changes
  function setupObservers() {
    // Observe for results section becoming active
    const resultsSection = document.getElementById('resultsSection');
    if (resultsSection) {
      // Call when results are shown
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && 
              mutation.attributeName === 'class' &&
              resultsSection.classList.contains('active')) {
            // Apply enhanced animations
            enhanceResultsPageAnimations();
            
            // Fix add to cart buttons
            setTimeout(enhanceSingleProductButtons, 800);
          }
        });
      });
      
      observer.observe(resultsSection, { attributes: true });
    }
  }
  
  // Run initialization
  initializeAnimations();
    // Mark animations as fully initialized
  window.sachiAnimations._initialized = true;
  console.log("Sachi Animations fully initialized (image animations removed)");
});
